import { FiX, FiAlertCircle, FiArrowLeft, FiActivity, FiSend, FiVolume2, FiVolumeX, FiMicOff, FiCornerUpLeft, FiDownload, FiTrash2, FiImage, FiSettings, FiMic, FiFileText, FiUser, FiPaperclip, FiCpu } from 'react-icons/fi';
import { FaFileExcel, FaRocket, FaFilePdf, FaFileWord, FaHeart, FaFileCsv } from 'react-icons/fa';
import { BsPinAngle } from 'react-icons/bs';
import { getIcon } from '../../utils/iconMap';
import MediaPickerModal from '../../components/common/MediaPickerModal';
import React, { useState, useRef, useEffect, forwardRef, useImperativeHandle } from 'react';
import SafeIcon from '../../common/SafeIcon';
import GnomeSettingsModal from './GnomeSettingsModal';
import { supabase } from '../../supabase';
import ConfirmDialog from '../common/ConfirmDialog';
import { FiRefreshCw } from 'react-icons/fi';
import { useLocation } from 'react-router-dom';
import { useSupabaseAuth } from '../../hooks/useSupabaseAuth';
import { useWorkspace } from '../../context/WorkspaceContext';
import { useGnomeContext } from '../../context/GnomeContext';
import { toast } from 'sonner';
import { useChat } from '@ai-sdk/react';
import { getGnomeName } from '../../utils/gnomeUtils';

const JEEVES_THOUGHTS = [
  "Ready to orchestrate some brilliance?",
  "The strategy engine is warmed up.",
  "I've cleared the deck for your genius.",
  "Shall we make some waves today?",
  "Your command is my code.",
  "Opportunity awaits your input.",
  "I've polished the pixels just for you.",
  "All systems nominal and ready for takeoff.",
  "Let's turn those ideas into reality.",
  "The digital realm is your oyster today."
];

// Helper to automatically capitalize sentences
const autoCapitalize = (text) => {
    if (!text) return text;
    return text.replace(/(^\s*[a-z])|([.!?]\s+[a-z])/g, match => match.toUpperCase());
};

// Helper to safely extract text from message (handles both content string and parts array)
const getMessageText = (msg) => {
    if (msg.content) return msg.content;
    if (msg.parts && Array.isArray(msg.parts)) {
        return msg.parts
            .filter(part => part.type === 'text')
            .map(part => part.text)
            .join('');
    }
    return '';
};

// Helper to render a single link or file card
const renderLinkOrCard = (url, linkText, key, onSaveToIntelligence) => {
    // Enhanced file detection: Check end of path OR query params indicating type
    const lowerUrl = url.toLowerCase();
    const isPdf = lowerUrl.includes('.pdf') || lowerUrl.includes('format=pdf');
    const isDoc = lowerUrl.includes('.doc') || lowerUrl.includes('.docx') || lowerUrl.includes('format=doc');
    const isXls = lowerUrl.includes('.xls') || lowerUrl.includes('.xlsx') || lowerUrl.includes('format=xls');
    const isCsv = lowerUrl.includes('.csv') || lowerUrl.includes('format=csv');

    const isVideo = lowerUrl.includes('.mp4') || lowerUrl.includes('.webm') || lowerUrl.includes('.mov') || lowerUrl.includes('.mkv');
    const isImage = lowerUrl.includes('.png') || lowerUrl.includes('.jpg') || lowerUrl.includes('.jpeg') || lowerUrl.includes('.gif') || lowerUrl.includes('.webp');

    if (isVideo) {
        return (
            <div key={key} className="my-2 max-w-sm rounded-lg overflow-hidden border border-charcoal-200">
                <video src={url} controls className="w-full h-auto" />
            </div>
        );
    }

    if (isImage) {
        return (
            <div key={key} className="my-2 max-w-sm rounded-lg overflow-hidden border border-charcoal-200">
                <img src={url} alt={linkText} className="w-full h-auto object-cover" />
            </div>
        );
    }

    if (isPdf || isDoc || isXls || isCsv) {
         // Extract filename from URL path if possible, else default
         let fileName = linkText !== url ? linkText : 'Document';
         // If linkText is just the URL, try to make it prettier
         if (fileName === url || fileName === 'Document') {
             try {
                const urlObj = new URL(url);
                const pathName = urlObj.pathname.split('/').pop();
                if (pathName && (pathName.includes('.') || pathName.length > 3)) {
                    fileName = pathName.replace(/_\d+\./, '.'); // Try to clean timestamp if typical format
                }
             } catch (e) {
                fileName = 'document.' + (isPdf ? 'pdf' : (isDoc ? 'docx' : (isXls ? 'xlsx' : 'csv')));
             }
         }

         const fileExt = isPdf ? 'pdf' : (isDoc ? 'docx' : (isXls ? 'xlsx' : 'csv'));
         const Icon = isPdf ? FaFilePdf : (isDoc ? FaFileWord : (isXls ? FaFileExcel : FaFileCsv));

         let colorClass = 'bg-charcoal-100 text-charcoal-600';
         if (isPdf) colorClass = 'bg-red-100 text-red-600';
         if (isDoc) colorClass = 'bg-blue-100 text-blue-600';
         if (isXls || isCsv) colorClass = 'bg-green-100 text-green-600';

         return (
            <div key={key} className="my-2 p-3 bg-charcoal-50 border border-charcoal-200 rounded-lg flex items-center justify-between group hover:border-blue-200 transition-colors max-w-sm">
                <div className="flex items-center space-x-3 overflow-hidden">
                     <div className={`p-2 rounded-lg ${colorClass}`}>
                         <Icon size={20} />
                     </div>
                     <div className="flex flex-col overflow-hidden">
                         <span className="text-sm font-medium text-charcoal-700 truncate" title={fileName}>{fileName}</span>
                         <span className="text-xs text-charcoal-400 uppercase">{fileExt} Document</span>
                     </div>
                </div>
                <div className="flex items-center space-x-1">
                     <a
                        href={url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-2 text-charcoal-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                        title="Download File"
                        onClick={(e) => e.stopPropagation()}
                     >
                        <FiDownload size={16} />
                     </a>
                     <button
                        onClick={(e) => {
                            e.stopPropagation();
                            onSaveToIntelligence(url, fileName);
                        }}
                        className="p-2 text-charcoal-400 hover:text-purple-600 hover:bg-purple-50 rounded-full transition-colors"
                        aria-label="Save to The Vault"
                        title="Save to The Vault"
                     >
                        <FiCpu size={16} />
                     </button>
                </div>
            </div>
         );
    }

    return (
        <a
            key={key}
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-blue-600 hover:underline break-all relative z-10 font-medium"
            onClick={(e) => e.stopPropagation()}
        >
            {linkText}
        </a>
    );
};

// Helper to render text with clickable links and file cards
const renderTextWithLinks = (text, onSaveToIntelligence) => {
    if (!text) return null;

    // 1. Split by Markdown Links: [Text](URL)
    // Regex: \[([^\]]+)\]\((https?:\/\/[^\s)]+)\)
    const markdownLinkRegex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)/g;

    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = markdownLinkRegex.exec(text)) !== null) {
        // Text before match
        if (match.index > lastIndex) {
            parts.push({ type: 'text', content: text.substring(lastIndex, match.index) });
        }
        // The match
        parts.push({ type: 'link', text: match[1], url: match[2] });
        lastIndex = markdownLinkRegex.lastIndex;
    }
    // Remaining text
    if (lastIndex < text.length) {
        parts.push({ type: 'text', content: text.substring(lastIndex) });
    }

    return parts.map((part, index) => {
        if (part.type === 'link') {
            return renderLinkOrCard(part.url, part.text, `md-${index}`, onSaveToIntelligence);
        } else {
            // Process raw URLs in text parts (fallback)
            const urlRegex = /(https?:\/\/[^\s)\]]+)/g;
            const subParts = part.content.split(urlRegex);
            return (
                <span key={`text-${index}`}>
                    {subParts.map((subPart, subIndex) => {
                         if (subPart.match(urlRegex)) {
                             return renderLinkOrCard(subPart, subPart, `raw-${index}-${subIndex}`, onSaveToIntelligence);
                         }
                         return subPart;
                    })}
                </span>
            );
        }
    });
};

const renderMessageContent = (text, onSaveToIntelligence, isUser) => {
    if (!text) return null;

    // Check for blockquotes at the start (simple implementation: contiguous > lines at start)
    const lines = text.split('\n');
    let quoteBuffer = [];
    let restBuffer = [];
    let readingQuote = true;

    for (let line of lines) {
        if (readingQuote && line.trim().startsWith('>')) {
            quoteBuffer.push(line.replace(/^>\s?/, ''));
        } else {
            readingQuote = false;
            restBuffer.push(line);
        }
    }

    const quoteContent = quoteBuffer.join('\n');
    const mainContent = restBuffer.join('\n').trim();

    const renderedMain = renderTextWithLinks(mainContent, onSaveToIntelligence);

    if (quoteContent) {
        const quoteClass = isUser
            ? "border-l-4 border-charcoal-600 pl-3 py-1 text-charcoal-300 text-xs italic bg-charcoal-800 rounded-r mb-2"
            : "border-l-4 border-charcoal-300 pl-3 py-1 text-charcoal-500 text-xs italic bg-charcoal-50 rounded-r mb-2";

        return (
            <div className="flex flex-col">
                <div className={quoteClass}>
                    {renderTextWithLinks(quoteContent, onSaveToIntelligence)}
                </div>
                <div>{renderedMain}</div>
            </div>
        );
    }

    return renderedMain;
};

const GnomeChat = forwardRef(({ gnome, onBack, hideHeader = false, refreshTrigger = 0, onListeningChange }, ref) => {
  const { user, session, loading: authLoading } = useSupabaseAuth();
  const { activeWorkspace } = useWorkspace();
  const { markGnomeNotificationsAsRead, gnomeNotifications, chatDrafts, setChatDraft } = useGnomeContext();
  const location = useLocation();
  const [loadingHistory, setLoadingHistory] = useState(false);
  const messagesEndRef = useRef(null);
  const scrollContainerRef = useRef(null);
  const [isAtBottom, setIsAtBottom] = useState(true);

  const [isFirstVisit, setIsFirstVisit] = useState(true);
  const [randomThought, setRandomThought] = useState('');
  const [showSettings, setShowSettings] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);

  const [showRefreshConfirm, setShowRefreshConfirm] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Local input state since useChat v2/v5 doesn't provide it
  const [localInput, setLocalInput] = useState(chatDrafts?.[gnome?.id] || '');
  const [isGenerating, setIsGenerating] = useState(false); // Manual loading state
  const textareaRef = useRef(null);
  const fileInputRef = useRef(null);
  const [attachment, setAttachment] = useState(null);
  const [uploadingAttachment, setUploadingAttachment] = useState(false);
  const [showMediaPicker, setShowMediaPicker] = useState(false);

  // Voice recording state
  const [isListening, setIsListening] = useState(false);
  const recognitionRef = useRef(null);

  useEffect(() => {
      if (onListeningChange) {
          onListeningChange(isListening);
      }
  }, [isListening, onListeningChange]);

  // Text-to-Speech state
  const [autoSpeak, setAutoSpeak] = useState(false);
  const [speakingMessageId, setSpeakingMessageId] = useState(null);
  const [isSynthesizing, setIsSynthesizing] = useState(false);
  const audioRef = useRef(null);

  // Initialize SpeechRecognition
  useEffect(() => {
    const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (SpeechRecognition) {
      recognitionRef.current = new SpeechRecognition();
      recognitionRef.current.continuous = true;
      recognitionRef.current.interimResults = true;

      recognitionRef.current.onresult = (event) => {
        let interimTranscript = '';
        let finalTranscript = '';

        for (let i = event.resultIndex; i < event.results.length; ++i) {
          if (event.results[i].isFinal) {
            finalTranscript += event.results[i][0].transcript;
          } else {
            interimTranscript += event.results[i][0].transcript;
          }
        }

        setLocalInput((prev) => {
          // If we had an ongoing session we might want to manage how final vs interim appends.
          // For simplicity, we can just append the final transcript if there is one.
          // The exact UX might need a separate state for interim, but appending final works well.
          if (finalTranscript) {
              return prev + (prev.endsWith(' ') ? '' : ' ') + finalTranscript;
          }
          return prev;
        });
      };

      recognitionRef.current.onend = () => {
        setIsListening(false);
      };

      recognitionRef.current.onerror = (event) => {
        console.error("Speech recognition error:", event.error);
        setIsListening(false);
      };
    }
  }, []);

  const toggleListening = () => {
    if (!recognitionRef.current) {
        toast.error("Speech recognition is not supported in your browser.");
        return;
    }

    if (isListening) {
        recognitionRef.current.stop();
        setIsListening(false);
    } else {
        try {
            recognitionRef.current.start();
            setIsListening(true);
            toast.info("Listening...");
        } catch (e) {
            console.error("Error starting speech recognition:", e);
        }
    }
  };

  // --- Text-to-Speech Functions ---
  const stopSpeaking = () => {
      if (audioRef.current) {
          audioRef.current.pause();
          audioRef.current.src = "";
          audioRef.current = null;
      }
      setSpeakingMessageId(null);
      setIsSynthesizing(false);
  };

  const speakText = async (text, messageId) => {
      // Stop any current speech
      stopSpeaking();

      if (!text) return;

      setIsSynthesizing(true);
      setSpeakingMessageId(messageId);

      try {
          const { data: { session } } = await supabase.auth.getSession();
          const { supabaseUrl } = await import('../../supabase');
          const response = await fetch(`${supabaseUrl}/functions/v1/gnome-tts`, {
              method: 'POST',
              headers: {
                  'Authorization': `Bearer ${session?.access_token}`,
                  'Content-Type': 'application/json'
              },
              body: JSON.stringify({ text, gnomeTitle: gnome.title })
          });

          if (!response.ok) {
              const errText = await response.text();
              throw new Error(`TTS Error: ${errText}`);
          }

          const audioBlob = await response.blob();
          const audioUrl = URL.createObjectURL(audioBlob);

          const audio = new Audio(audioUrl);
          audioRef.current = audio;

          audio.onended = () => {
              URL.revokeObjectURL(audioUrl);
              setSpeakingMessageId(null);
          };

          audio.onerror = (e) => {
              console.error("Audio playback error", e);
              URL.revokeObjectURL(audioUrl);
              setSpeakingMessageId(null);
          };

          setIsSynthesizing(false);
          await audio.play();

      } catch (err) {
          console.error("Failed to speak text:", err);
          toast.error("Failed to generate voice.");
          setIsSynthesizing(false);
          setSpeakingMessageId(null);
      }
  };

  // Stop speaking when unmounting or changing gnomes
  useEffect(() => {
      return () => stopSpeaking();
  }, [gnome.id]);

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${Math.min(textareaRef.current.scrollHeight, 120)}px`;
    }
  }, [localInput]);

  // Sync localInput changes to context chatDrafts on unmount
  const localInputRef = useRef(localInput);

  useEffect(() => {
    localInputRef.current = localInput;
  }, [localInput]);

  useEffect(() => {
    return () => {
      if (gnome?.id) {
          setChatDraft(gnome.id, localInputRef.current);
      }
    };
  }, [gnome?.id, setChatDraft]);

  // Handle gnome switching
  useEffect(() => {
      setLocalInput(chatDrafts?.[gnome?.id] || '');
  }, [gnome?.id]);

  // --- ROBUST URL RESOLUTION ---
  // The user reported issues with Vercel env vars.
  // We perform a multi-layered check to find the correct Supabase URL.

  // --- ROBUST URL RESOLUTION ---
  // The user reported issues with Vercel env vars defaulting to app URL.
  // We explicitly use the Supabase URL to ensure connection to Edge Functions.
  const HARDCODED_URL = 'https://zvpxmxcznimhwhxjnhvn.supabase.co';

  // We prioritize the hardcoded URL to prevent misconfigured env vars from breaking chat.
  const resolvedSupabaseUrl = HARDCODED_URL;

  // Debug log for troubleshooting in production console
  useEffect(() => {
      // console.log("GnomeChat: Using API URL:", `${resolvedSupabaseUrl}/functions/v1/gnome-chat`);
  }, [resolvedSupabaseUrl]);
  // -----------------------------

  const isMockMode = false; // We are disabling mock mode to force Edge Function usage

  const apiUrl = `${resolvedSupabaseUrl}/functions/v1/gnome-chat`;

  // Initialize Vercel AI SDK hook for state management only
  const chatHelpers = useChat({
    id: `${activeWorkspace?.id}-${gnome?.id}`,
    api: apiUrl,
    headers: {
        Authorization: session ? `Bearer ${session.access_token}` : '',
    },
    onError: (err) => {
      // console.error('Chat error:', err);
      // Basic error handling for initial load issues
      if (err.message?.includes('401')) {
           toast.error("Authentication failed. Please refresh the page.");
      }
    }
  });

  // Destructure what we need
  const { messages, setMessages, status } = chatHelpers;

  const isLoading = status === 'submitted' || status === 'streaming' || isGenerating;

  // Auto-speak new assistant messages
  const prevIsLoadingRef = useRef(isLoading);
  useEffect(() => {
      const wasLoading = prevIsLoadingRef.current;
      if (wasLoading && !isLoading && autoSpeak && messages.length > 0) {
          const lastMessage = messages[messages.length - 1];
          if (lastMessage.role === 'assistant') {
              // Wait a tiny bit to let the DOM settle, though not strictly necessary for TTS
              setTimeout(() => {
                  speakText(getMessageText(lastMessage), lastMessage.id);
              }, 100);
          }
      }
      prevIsLoadingRef.current = isLoading;
  }, [isLoading, autoSpeak, messages, gnome.title]);

  const scrollToBottom = (behavior = "smooth") => {
    messagesEndRef.current?.scrollIntoView({ behavior });
  };

  const scrollToLastMessageStart = (behavior = "smooth") => {
      // Find the last message element by ID
      if (messages.length > 0) {
          const lastMsg = messages[messages.length - 1];
          const element = document.getElementById(`msg-${lastMsg.id}`);
          if (element) {
              element.scrollIntoView({ behavior, block: 'start' });
          } else {
              // Fallback if element not found yet
              scrollToBottom(behavior);
          }
      }
  };

  useEffect(() => {
    // If new message added (length increased) or streaming (isLoading changed)
    // We want to follow the stream but respect user scroll if they move up?
    // For simplicity, auto-scroll to bottom on new content is standard.
    if (messages.length > 0 && isLoading) {
        scrollToBottom();
    }
  }, [messages, isLoading]);

  // Ensure scroll happens after history load specifically
  useEffect(() => {
      if (!loadingHistory && messages.length > 0) {
          // On initial load, scroll to the START of the last message so user reads from top down
          // Use 'auto' for instant jump without animation disorienting user
          setTimeout(() => {
             scrollToLastMessageStart("auto");
          }, 100);
      }
  }, [loadingHistory]);

  // Handle Jeeves intro/random thought logic
  useEffect(() => {
    const hasSeen = localStorage.getItem('jeeves_intro_shown');
    if (hasSeen) {
      setIsFirstVisit(false);
      setRandomThought(JEEVES_THOUGHTS[Math.floor(Math.random() * JEEVES_THOUGHTS.length)]);
    } else {
      localStorage.setItem('jeeves_intro_shown', 'true');
      setIsFirstVisit(true);
    }
  }, []);

  // Load chat history from Supabase on mount/gnome change
  useEffect(() => {
    const fetchHistory = async () => {
      if (!activeWorkspace || gnome?.id === undefined || gnome?.id === null) return;

      setLoadingHistory(true);
      try {
        // Fetch history for this specific gnome
        const { data, error } = await supabase
          .from('chat_messages_with_profiles')
          .select('*')
          .eq('workspace_id', activeWorkspace.id)
          .eq('gnome_id', gnome.id.toString())
          .order('created_at', { ascending: false })
          .limit(50);

        if (error) throw error;

        let formattedMessages = [];

        if (data && data.length > 0) {
          // Format messages for AI SDK (role: 'user' | 'assistant')
          // Deduplicate consecutive identical messages (client-side safety net)
          formattedMessages = data.reverse().reduce((acc, msg) => {
             const prev = acc[acc.length - 1];
             const isDup = prev && prev.content === msg.content && prev.role === (msg.role === 'gnome' ? 'assistant' : msg.role);
             if (!isDup) {
                 acc.push({
                    id: msg.id,
                    role: msg.role === 'gnome' ? 'assistant' : msg.role,
                    content: msg.content,
                    createdAt: new Date(msg.created_at),
                    gnomeId: msg.gnome_id,
                    reaction: msg.metadata?.reaction,
                    reactions: msg.metadata?.reactions || (msg.metadata?.reaction ? [msg.metadata?.reaction] : []),
                   persona: msg.metadata?.persona,
                    userFullName: msg.user_full_name,
                    userAvatarUrl: msg.user_avatar_url,
                    userId: msg.user_id
                 });
             }
             return acc;
          }, []);
        }

        setMessages(formattedMessages);

      } catch (err) {
        // console.error('Error fetching chat history:', err);
      } finally {
        setLoadingHistory(false);
      }
    };

    fetchHistory();

    // Set up realtime subscription for new incoming messages from background tasks
    const subscription = supabase
      .channel(`chat_messages:${activeWorkspace.id}:${gnome.id}`)
      .on('postgres_changes', {
        event: 'INSERT',
        schema: 'public',
        table: 'chat_messages',
        filter: `workspace_id=eq.${activeWorkspace.id}`
      }, (payload) => {
        // Ensure message belongs to the current gnome
        if (payload.new && payload.new.gnome_id === gnome.id.toString()) {
            setMessages(prev => {
                // Deduplicate to avoid adding a message already in the state
                if (prev.some(m => m.id === payload.new.id)) return prev;

                // Also deduplicate by content and role (e.g. optimistic UI updates that were just saved)
                const isOptimisticDup = prev.some(m => m.content === payload.new.content && m.role === (payload.new.role === 'gnome' ? 'assistant' : payload.new.role));
                if (isOptimisticDup) return prev;

                const newMsg = {
                    id: payload.new.id,
                    role: payload.new.role === 'gnome' ? 'assistant' : payload.new.role,
                    content: payload.new.content,
                    createdAt: new Date(payload.new.created_at),
                    gnomeId: payload.new.gnome_id,
                    reaction: payload.new.metadata?.reaction,
                    reactions: payload.new.metadata?.reactions || (payload.new.metadata?.reaction ? [payload.new.metadata?.reaction] : []),
                    persona: payload.new.metadata?.persona,
                    userFullName: payload.new.user_full_name,
                    userAvatarUrl: payload.new.user_avatar_url,
                    userId: payload.new.user_id
                };
                return [...prev, newMsg];
            });
        }
      })
      .subscribe();

    return () => {
        subscription.unsubscribe();
    };
  }, [activeWorkspace, gnome?.id, setMessages, refreshTrigger]);

  const handleScroll = () => {
    if (!scrollContainerRef.current) return;
    const { scrollTop, scrollHeight, clientHeight } = scrollContainerRef.current;
    // Consider it "at bottom" if within 50px of the actual bottom
    const atBottom = scrollHeight - scrollTop - clientHeight < 50;
    setIsAtBottom(atBottom);
  };

  // Mark notifications as read when chat is open and we have unread notifications for this gnome AND user has scrolled to bottom
  useEffect(() => {
      if (!activeWorkspace || gnome?.id === undefined || gnome?.id === null) return;

      // Check if we actually have unread notifications for this gnome
      const hasUnread = gnomeNotifications.some(n => String(n.gnome_id) === String(gnome.id));

      if (hasUnread && isAtBottom) {
          // console.log(`GnomeChat: Marking notifications as read for Gnome ${gnome.id}`);
          markGnomeNotificationsAsRead(gnome.id);
      }
  }, [activeWorkspace, gnome?.id, gnomeNotifications, markGnomeNotificationsAsRead, isAtBottom]);


  const clearChat = async () => {
    if (!activeWorkspace || !gnome) return;
    if (!window.confirm("Are you sure you want to clear this chat history?")) return;

    try {
        const { error } = await supabase
            .from('chat_messages')
            .delete()
            .eq('workspace_id', activeWorkspace.id)
            .eq('gnome_id', gnome.id.toString());

        if (error) throw error;
        setMessages([]);
        toast.success("Chat history cleared.");
    } catch (err) {
        // console.error("Error clearing chat:", err);
        toast.error("Failed to clear chat.");
    }
  };

  const handleSaveToIntelligence = async (url, fileName) => {
      if (!activeWorkspace) return;

      const lowerUrl = url.toLowerCase();
      const isPdf = lowerUrl.includes('.pdf') || lowerUrl.includes('format=pdf');
      const isDoc = lowerUrl.includes('.doc') || lowerUrl.includes('.docx') || lowerUrl.includes('format=doc');
      const isXls = lowerUrl.includes('.xls') || lowerUrl.includes('.xlsx') || lowerUrl.includes('format=xls');
      const isCsv = lowerUrl.includes('.csv') || lowerUrl.includes('format=csv');

      const toastId = toast.loading(`Saving ${fileName} to The Vault...`);
      try {
          // Use train-brain (legacy function name) to index the document URL
          const { data, error } = await supabase.functions.invoke('train-brain', {
              body: {
                  workspaceId: activeWorkspace.id,
                  newData: {
                      type: 'url', // Use URL type so it fetches and extracts content
                      url: url,
                      fileType: (isPdf || isDoc || isXls || isCsv) ? 'document' : 'website' // pass fileType explicitly
                  }
              }
          });

          if (error) {
              // Try to parse the error message if it's a JSON response from the edge function
              let errorMsg = error.message;
              if (error.context && error.context.status === 400) {
                  try {
                      // Attempt to extract the custom error message we send in the JSON body
                      const bodyObj = await error.context.json();
                      if (bodyObj && bodyObj.error) {
                          errorMsg = bodyObj.error;
                      }
                  } catch (e) {
                     // Could be a string or fail to parse, fallback to original message
                     if (typeof error.context.body === 'string') {
                         try {
                             const parsed = JSON.parse(error.context.body);
                             if (parsed.error) errorMsg = parsed.error;
                         } catch (e2) { /* ignore parse error */ }
                     }
                  }
              }
              throw new Error(errorMsg);
          }

          toast.success("Document saved to The Vault!", { id: toastId });
      } catch (err) {
          // console.error("Failed to save to intelligence:", err);
          toast.error(err.message || "Failed to save document to Intelligence.", { id: toastId });
      }
  };


  const handlePinTask = async (content) => {
      if (!activeWorkspace || !user) return;
      const toastId = toast.loading("Pinning task...");
      try {
          // Ensure team meeting chat does not trigger a foreign key error
          let safeGnomeId = parseInt(gnome.id);
          if (isNaN(safeGnomeId) || safeGnomeId === 0) {
              safeGnomeId = null;
          }

          const { error } = await supabase.from('user_pins').insert({
              workspace_id: activeWorkspace.id,
              gnome_id: safeGnomeId,
              content: content,
              user_id: user.id
          });

          if (error) throw error;
          toast.success("Task pinned to Dashboard!", { id: toastId });

          // Optionally trigger an event or update state if needed globally
      } catch (err) {
          // console.error("Failed to pin task:", err);
          toast.error("Failed to pin task.", { id: toastId });
      }
  };


  const handleSaveTextToIntelligence = async (content) => {
      if (!activeWorkspace || !content) return;

      const toastId = toast.loading(`Saving insight to The Vault...`);
      try {
          const { error } = await supabase.functions.invoke('train-brain', {
              body: {
                  workspaceId: activeWorkspace.id,
                  newData: {
                      type: 'text',
                      content: content
                  }
              }
          });

          if (error) throw error;
          toast.success("Insight saved to The Vault!", { id: toastId });
      } catch (err) {
          // console.error("Failed to save text to intelligence:", err);
          toast.error("Failed to save insight.", { id: toastId });
      }
  };

  const handleGenerateDocument = async (content) => {
      if (!activeWorkspace || !content) return;
      const toastId = toast.loading("Generating document...");

      try {
          const { data, error } = await supabase.functions.invoke('generate-document', {
              body: {
                  content: content,
                  title: `Gnome Chat - ${new Date().toLocaleDateString()}`,
                  format: 'docx',
                  workspaceId: activeWorkspace.id,
                  brandName: activeWorkspace.name
              }
          });

          if (error) throw error;
          if (data && data.url) {
              window.open(data.url, '_blank');
              toast.success("Document generated!", { id: toastId });
          } else {
              throw new Error("No URL returned");
          }
      } catch (err) {
          // console.error("Failed to generate document:", err);
          toast.error("Failed to generate document.", { id: toastId });
      }
  };

  const handleReaction = async (msgId, reaction, content) => {
      if (!activeWorkspace) return;

      // Find the message first to calculate newReactions purely before state update
      const targetMsg = messages.find(m => m.id === msgId);
      if (!targetMsg) return;

      const currentReactions = Array.isArray(targetMsg.reactions) ? targetMsg.reactions :
                              (targetMsg.reaction ? [targetMsg.reaction] : []);

      let newReactions = [];
      if (currentReactions.includes(reaction)) {
          // Toggle off
          newReactions = currentReactions.filter(r => r !== reaction);
      } else {
          // Toggle on
          newReactions = [...currentReactions, reaction];
      }

      // Optimistic Update
      setMessages(prev => prev.map(m => {
          if (m.id === msgId) {
              return { ...m, reactions: newReactions, reaction: newReactions[0] || null }; // Keep `reaction` for backward compatibility temporarily if needed
          }
          return m;
      }));

      try {
          // Fetch current metadata to preserve other fields
          const { data: currentMsg, error: fetchError } = await supabase
              .from('chat_messages')
              .select('metadata')
              .eq('id', msgId)
              .single();

          if (fetchError) throw fetchError;

          const updatedMetadata = {
              ...(currentMsg.metadata || {}),
              reactions: newReactions,
              reaction: newReactions[0] || null
          };

          const { error: updateError } = await supabase
              .from('chat_messages')
              .update({ metadata: updatedMetadata })
              .eq('id', msgId);

          if (updateError) throw updateError;

      } catch (err) {
          toast.error("Failed to save reaction.");
          // Rollback optimistic update is complex here without saving previous state,
          // a simpler fallback is to just remove the failed reaction change.
          setMessages(prev => prev.map(m => {
              if (m.id === msgId) {
                  const currentReactions = Array.isArray(m.reactions) ? m.reactions : [];
                  const rolledBackReactions = newReactions.includes(reaction)
                      ? currentReactions.filter(r => r !== reaction) // if we tried to add, remove
                      : [...currentReactions, reaction]; // if we tried to remove, add back
                  return { ...m, reactions: rolledBackReactions, reaction: rolledBackReactions[0] || null };
              }
              return m;
          }));
      }
  };

  const handleRefresh = async () => {
      setShowRefreshConfirm(false);
      setMessages([]);
      toast.success("Conversation refreshed! Starting fresh.");

      const welcomeMsg = {
          id: Date.now().toString(),
          role: 'assistant',
          content: 'Hello! Team Meeting here. ☕🍄 Ready to start fresh. What can I help you with today?'
      };
      setMessages([welcomeMsg]);
  };

  const handleDelete = async () => {
      if (!activeWorkspace || !gnome) return;
      setShowDeleteConfirm(false);

      try {
          const { error } = await supabase
              .from('chat_messages')
              .delete()
              .eq('workspace_id', activeWorkspace.id)
              .eq('gnome_id', gnome.id);

          if (error) throw error;

          setMessages([]);
          toast.success("Conversation deleted permanently.");

          const welcomeMsg = {
              id: Date.now().toString(),
              role: 'assistant',
              content: 'Hello! Team Meeting here. ☕🍄 What can I help you with today?'
          };
          setMessages([welcomeMsg]);
      } catch (err) {
          console.error("Failed to delete conversation:", err);
          toast.error("Failed to delete. Please try again.");
      }
  };

  const runDiagnostics = async () => {
       const diagMsgId = 'diag-' + Date.now();
       setMessages(prev => [...prev, {
          id: diagMsgId,
          role: 'assistant',
          content: 'Running system diagnostics... 🕵️‍♂️',
          createdAt: new Date(),
          gnomeId: gnome.id
       }]);

       try {
          const [chatHealth, socialHealth] = await Promise.all([
              supabase.functions.invoke('gnome-chat', { method: 'GET' }),
              supabase.functions.invoke('generate-social-content', { method: 'GET' })
          ]);

          const formatHealth = (name, result) => {
              if (result.error) return `❌ ${name}: Error (${result.error.message || 'Unknown'})`;
              const data = result.data;
              if (!data) return `❌ ${name}: No Data`;

              let report = `✅ ${name} (${data.status})\n`;
              if (data.checks) {
                  Object.entries(data.checks).forEach(([key, val]) => {
                       if (key === 'db_error') {
                           report += `   - ${key}: ${val ? `FAIL (${val})` : 'OK'}\n`;
                       } else {
                           report += `   - ${key}: ${val ? 'OK' : 'MISSING/FAIL'}\n`;
                       }
                  });
              }
              if (data.timestamp) report += `   (Time: ${data.timestamp})\n`;
              return report;
          };

          const report = `**System Diagnostic Report**\n\n${formatHealth('Chat & Database', chatHealth)}\n${formatHealth('Veo & Social', socialHealth)}`;

          setMessages(prev => prev.map(m => m.id === diagMsgId ? { ...m, content: report } : m));
       } catch (e) {
          // console.error(e);
          setMessages(prev => prev.map(m => m.id === diagMsgId ? { ...m, content: `Diagnostic failed: ${e.message}` } : m));
       }
  };

  useImperativeHandle(ref, () => ({
    runDiagnostics,
    openSettings: () => setShowSettings(true),
    toggleAutoSpeak: () => {
        setAutoSpeak(prev => {
            if (prev) stopSpeaking();
            return !prev;
        });
    },
    getAutoSpeakState: () => autoSpeak,
    toggleListening: () => toggleListening(),
    getListeningState: () => isListening
  }));

  // Helper to handle form submission with workspace check

  const handleAttachmentChange = async (e) => {
      const file = e.target.files[0];
      if (!file || !activeWorkspace) return;

      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'image/jpeg', 'image/png', 'image/webp'];
      if (!allowedTypes.includes(file.type)) {
          toast.error("Unsupported file type. Please upload PDF, DOCX, or Images.");
          return;
      }

      setUploadingAttachment(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `${activeWorkspace.id}/${fileName}`;

      try {
          const arrayBuffer = await file.arrayBuffer();

          const { error: uploadError } = await supabase.storage
              .from('ad-creatives') // Storing in ad-creatives or a new bucket
              .upload(filePath, arrayBuffer, {
                  contentType: file.type,
                  upsert: true
              });

          if (uploadError) throw uploadError;

          const { data: { publicUrl } } = supabase.storage
              .from('ad-creatives')
              .getPublicUrl(filePath);

          setAttachment({
              url: publicUrl,
              name: file.name,
              type: file.type
          });
      } catch (err) {
          toast.error("Failed to upload attachment.");
      } finally {
          setUploadingAttachment(false);
          if (fileInputRef.current) fileInputRef.current.value = '';
      }
  };


  const handleAssetSelect = (asset) => {
      setAttachment({
          url: asset.type === 'video' ? asset.video_url : asset.image_url,
          name: asset.name,
          type: asset.type === 'video' ? 'video/mp4' : 'image/jpeg' // Approximation for display
      });
      setShowMediaPicker(false);
  };

  const removeAttachment = () => {
      setAttachment(null);
  };
const onFormSubmit = async (e, customContent = null) => {
    if (e) e.preventDefault();

    const content = customContent !== null ? customContent : localInput;

    if (!activeWorkspace) {
      toast.error("No active workspace selected.");
      return;
    }

    // Check auth loading state to prevent race conditions
    if (authLoading) {
        toast.info("Connecting to secure service...");
        return;
    }

    if (!content.trim()) return;

    if (customContent === null) {
        setLocalInput(''); // Optimistic clear
        if (gnome?.id) {
            setChatDraft(gnome.id, '');
        }
    }
    setAttachment(null);
    if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
    }

    // --- Diagnostic Command Interception ---
    if (content.trim() === '/diagnose' || content.trim() === '/status') {
         await runDiagnostics();
         return; // Stop normal submission
    }
    // ---------------------------------------

    // Handle Reply Format
    let contentToSend = content;
    if (attachment) {
        if (attachment.type.startsWith('image/')) {
            contentToSend = `${content}\n![Attached Image](${attachment.url})`;
        } else {
            contentToSend = `${content}\n[Attached File: ${attachment.name}](${attachment.url})`;
        }
    }
    if (replyingTo) {
        const quoted = getMessageText(replyingTo).split('\n').map(line => `> ${line}`).join('\n');
        contentToSend = `${quoted}\n\n${content}`;
        setReplyingTo(null);
    }

    // Optimistic Update: Add User Message
    const newUserMsg = {
        id: Date.now().toString(),
        role: 'user',
        content: contentToSend,
        createdAt: new Date(),
        gnomeId: gnome.id,
        userAvatarUrl: user?.user_metadata?.avatar_url,
        userId: user?.id,
        userFullName: user?.user_metadata?.full_name || user?.user_metadata?.name || 'User'
    };

    // We must pass the updated history to the backend
    const updatedMessages = [...messages, newUserMsg];
    setMessages(updatedMessages);
    setIsGenerating(true);

    try {
        const response = await fetch(apiUrl, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                Authorization: session ? `Bearer ${session.access_token}` : '',
            },
            body: JSON.stringify({
                messages: updatedMessages,
                gnomeId: gnome.id,
                workspaceId: activeWorkspace?.id,
                context: { path: location.pathname }
            })
        });

        if (!response.ok) {
            let errorMsg = `Server returned ${response.status}`;
            try {
                // Try to parse JSON error body first
                const errBody = await response.clone().json();
                if (errBody && errBody.error) {
                    errorMsg = errBody.error;
                } else if (errBody && errBody.message) {
                    errorMsg = errBody.message;
                }
            } catch (e) {
                // Fallback to text if JSON fails
                const errText = await response.text();
                if (errText && errText.length < 200) {
                     errorMsg = errText;
                }
            }
            throw new Error(errorMsg);
        }

        let text = '';
        let msgId = (Date.now() + 1).toString(); // Fallback ID
        let msgPersona = null;

        // Parse JSON response which now includes ID
        const contentType = response.headers.get('content-type');
        if (contentType && contentType.includes('application/json')) {
            try {
                const data = await response.json();
                // Handle new format { response: string, id: uuid }
                text = data.response || data.content || '';
                if (data.id) msgId = data.id;
                if (data.persona) msgPersona = data.persona;
            } catch (e) {
                // console.warn("Failed to parse JSON response:", e);
                text = await response.text(); // Fallback
            }
        } else {
             // Fallback for legacy plain text response
             text = await response.text();
        }

        if (!text || text.trim().length === 0) {
            // console.warn("GnomeChat: Backend returned empty text.");
            toast.warning("Received empty response from AI.");
        } else {
            // Success: Add Assistant Message
            const assistantMsg = {
                id: msgId,
                role: 'assistant',
                content: text,
                createdAt: new Date(),
            gnomeId: gnome.id,
            persona: msgPersona // applied immediately
            };
            setMessages(prev => [...prev, assistantMsg]);
        }
    } catch (err) {
        // console.error("Failed to send message (manual fetch):", err);
        toast.error(`Message failed: ${err.message}`);
        // Optional: Remove the optimistic user message on failure?
        // setMessages(prev => prev.filter(m => m.id !== newUserMsg.id));
    } finally {
        setIsGenerating(false);
    }
  };

  // Helper to resolve icon from string
  const resolveIcon = (iconName) => {
      if (typeof iconName === 'string') {
          return getIcon(iconName) || getIcon(iconName) || getIcon(iconName) || FiAlertCircle;
      }
      return iconName;
  };

  // Resolve icon for header/display if it's a string
  let DisplayIcon = resolveIcon(gnome.icon);

  return (
    <div className="flex flex-col h-full bg-white rounded-xl shadow-sm border border-charcoal-200 overflow-hidden">
      {/* Header */}
      {!hideHeader && (
        <div className="p-4 border-b border-charcoal-200 flex items-center justify-between bg-charcoal-50">
          <div className="flex items-center">
            {onBack && (
              <button
                onClick={onBack}
                className="mr-3 p-2 rounded-full hover:bg-charcoal-200 text-charcoal-500 transition-colors"
                aria-label="Back"
              >
                <FiArrowLeft />
              </button>
            )}
            <div className={`w-10 h-10 rounded-lg ${gnome.color} flex items-center justify-center text-white mr-3 overflow-hidden`}>
              {gnome.id === '0' || gnome.id === 0 ? (
                <img src="/shroom-logo.webp" alt="Team" className="w-full h-full object-cover" />
              ) : (gnome.headshot || gnome.image) ? (
                <img src={gnome.headshot || gnome.image} alt={gnome.title} className="w-full h-full object-cover" />
              ) : (
                <SafeIcon icon={DisplayIcon} />
              )}
            </div>
            <div>
              <h3 className="font-bold text-charcoal-900">{getGnomeName(gnome.title)}</h3>
              <p className="text-xs text-charcoal-500">{gnome.focus}</p>
            </div>
          </div>
           <div className="flex items-center space-x-2">
             {/* Auto-Speak Toggle Button */}
             <button
                 type="button"
                 onClick={toggleListening}
                 className={`p-2 rounded-full transition-colors ${isListening ? 'text-red-500 bg-red-50 animate-pulse' : 'text-charcoal-400 hover:text-charcoal-900 hover:bg-charcoal-200'}`}
                 title={isListening ? "Stop listening" : "Start voice input"}
                 aria-label={isListening ? "Stop listening" : "Start voice input"}
             >
                 {isListening ? <FiMicOff /> : <FiMic />}
             </button>
             <button
               onClick={() => {
                   setAutoSpeak(!autoSpeak);
                   if (autoSpeak) stopSpeaking();
               }}
               className={`p-2 rounded-full transition-colors ${autoSpeak ? 'text-blue-500 bg-blue-50' : 'text-charcoal-400 hover:text-charcoal-900 hover:bg-charcoal-200'}`}
               aria-label={autoSpeak ? "Disable auto-read" : "Enable auto-read responses"}
               title={autoSpeak ? "Disable auto-read" : "Enable auto-read responses"}
             >
               {autoSpeak ? <FiVolume2 /> : <FiVolumeX />}
             </button>
             {/* Settings Button */}
             <button
               onClick={() => setShowSettings(true)}
               className="p-2 text-charcoal-400 hover:text-charcoal-900 hover:bg-charcoal-200 rounded-full transition-colors"
               aria-label="Gnome Settings (Mode & Guardrails)"
               title="Gnome Settings (Mode & Guardrails)"
             >
               <FiSettings />
             </button>
             {/* Diagnostics Button */}
             <button
               onClick={() => runDiagnostics()}
               className="p-2 text-charcoal-400 hover:text-blue-500 hover:bg-blue-50 rounded-full transition-colors"
               aria-label="System Status / Diagnostics"
               title="System Status / Diagnostics"
             >
               <FiActivity />
             </button>
             {/* Clear Chat Button */}
             {gnome.id !== '0' && gnome.id !== 0 && (
               <button
                 onClick={clearChat}
                 className="p-2 text-charcoal-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                 aria-label="Clear Chat History"
                 title="Clear Chat History"
               >
                 <FiTrash2 />
               </button>
             )}
             {/* Team Meeting Exclusive Controls */}
             {(gnome.id === '0' || gnome.id === 0) && (
               <>
                 <button
                   onClick={() => setShowRefreshConfirm(true)}
                   className="p-2 text-charcoal-400 hover:text-blue-600 hover:bg-blue-50 rounded-full transition-colors"
                   aria-label="Refresh conversation"
                   title="Start fresh conversation"
                 >
                   <FiRefreshCw />
                 </button>
                 <button
                   onClick={() => setShowDeleteConfirm(true)}
                   className="p-2 text-charcoal-400 hover:text-red-600 hover:bg-red-50 rounded-full transition-colors"
                   aria-label="Delete conversation history"
                   title="Delete conversation history"
                 >
                   <FiTrash2 />
                 </button>
               </>
             )}
           </div>
        </div>
      )}

      {showRefreshConfirm && (
        <ConfirmDialog
          title="Start Fresh Conversation?"
          message="This will clear the current chat window and start a new conversation."
          confirmText="Start Fresh"
          cancelText="Cancel"
          icon="🔄"
          onConfirm={handleRefresh}
          onCancel={() => setShowRefreshConfirm(false)}
        />
      )}

      {showDeleteConfirm && (
        <ConfirmDialog
          title="Delete Entire Conversation?"
          message={"This will permanently delete ALL conversation history for this chat.\n\nThis action CANNOT be undone.\n\nType \"DELETE\" to confirm:"}
          confirmText="Delete Permanently"
          cancelText="Cancel"
          requiresTyping={true}
          typingConfirmation="DELETE"
          dangerMode={true}
          icon="⚠️"
          onConfirm={handleDelete}
          onCancel={() => setShowDeleteConfirm(false)}
        />
      )}

      {/* Settings Modal */}
      <GnomeSettingsModal
        gnome={gnome}
        isOpen={showSettings}
        onClose={() => setShowSettings(false)}
      />


      {/* Media Picker Modal */}
      {showMediaPicker && (
          <MediaPickerModal
              isOpen={showMediaPicker}
              onClose={() => setShowMediaPicker(false)}
              onSelect={handleAssetSelect}
              typeFilter="all"
          />
      )}

      {/* Messages Area */}
      <div
        ref={scrollContainerRef}
        onScroll={handleScroll}
        className="flex-1 overflow-y-auto p-4 space-y-4 bg-charcoal-50/50"
      >
        {loadingHistory ? (
            <div className="flex justify-center items-center h-full text-charcoal-400">
                Loading history...
            </div>
        ) : messages.length === 0 ? (
           // Welcome state
            <div className="flex flex-col items-center justify-center h-full text-center p-6 opacity-60">
                <div className={`w-16 h-16 rounded-2xl ${gnome.color} flex items-center justify-center text-white mb-4 text-2xl shadow-lg overflow-hidden`}>
                    {(gnome.headshot || gnome.image) ? (
                        <img src={gnome.headshot || gnome.image} alt={gnome.title} className="w-full h-full object-cover" />
                    ) : (
                        <SafeIcon icon={DisplayIcon} />
                    )}
                </div>
                <h4 className="text-lg font-bold text-charcoal-700">
                   {gnome.id === '0' || gnome.id === 0 ? `Welcome to the ${getGnomeName(gnome.title)}.` : `Hello! I'm ${getGnomeName(gnome.title)}.`}
                </h4>
                {gnome.id === '0' || gnome.id === 0 ? (
                    <p className="text-charcoal-500 mt-2">Mention any gnome to pull them in, or just drop a message and we'll figure out who is best suited to help.</p>
                ) : getGnomeName(gnome.title) === 'Jeeves' ? (
                   <div className="text-charcoal-500 mt-2 space-y-1">
                      {isFirstVisit ? (
                        <>
                          <p>Happy {new Date().toLocaleDateString('en-US', { weekday: 'long' })}... assuming you brought coffee?</p>
                          <p className="text-sm italic">"What's on our agenda today?"</p>
                        </>
                      ) : (
                        <p>
                          Welcome back, {user?.user_metadata?.full_name?.split(' ')[0] || user?.user_metadata?.name?.split(' ')[0] || 'there'}. {randomThought}
                        </p>
                      )}
                   </div>
                ) : (
                  <p className="text-charcoal-500 mt-2">How can I help you with your {gnome.focus} today?</p>
                )}
            </div>
        ) : (
            <>
                {messages.map((msg) => (
                  <div
                    key={msg.id}
                    id={`msg-${msg.id}`}
                    className={`flex group ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
                  >
                    <div className={`flex max-w-[85%] ${msg.role === 'user' ? 'flex-row-reverse' : 'flex-row'}`}>
                      <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${msg.role === 'user' ? 'bg-charcoal-200 ml-2' : `${msg.persona?.color || gnome.color} text-white mr-2`}`}>
                        {msg.role === 'user' ? (
                          msg.userAvatarUrl || (msg.userId === user?.id && (user?.user_metadata?.avatar_url || user?.user_metadata?.picture)) ? (
                            <img src={msg.userAvatarUrl || user.user_metadata.avatar_url || user.user_metadata.picture} alt="User" className="w-full h-full object-cover" />
                          ) : (
                            <FiUser className="text-charcoal-600" />
                          )
                        ) : (msg.persona?.headshot || msg.persona?.image) ? (
                          <img src={msg.persona.headshot || msg.persona.image} alt={msg.persona.title || 'Persona'} className="w-full h-full object-cover" />
                        ) : Number(msg.gnomeId) === 0 && !msg.persona ? (
                          <img src="/shroom-logo.webp" alt="Team Chat" className="w-full h-full object-cover p-1" />
                        ) : (
                          (gnome.headshot || gnome.image) ? (
                            <img src={gnome.headshot || gnome.image} alt={gnome.title} className="w-full h-full object-cover" />
                          ) : (
                            <SafeIcon icon={resolveIcon(msg.persona?.icon || gnome.icon)} className="text-sm" />
                          )
                        )}
                      </div>

                      <div className={`p-3 rounded-2xl text-sm leading-relaxed whitespace-pre-wrap ${
                        msg.role === 'user'
                          ? 'bg-charcoal-900 text-white rounded-tr-none'
                          : 'bg-white border border-charcoal-200 text-charcoal-700 rounded-tl-none shadow-sm'
                      }`}>
                         {/* Visual Attribution for Cross-Gnome Messages */}
                         {msg.role !== 'user' && msg.gnomeId && (msg.gnomeId.toString() !== gnome.id.toString() || msg.persona || Number(msg.gnomeId) === 0) && (
                            <div className={`text-[10px] font-bold ${msg.persona?.color ? msg.persona.color.replace('bg-', 'text-') : 'text-charcoal-400'} mb-1 uppercase tracking-wider`}>
                                {msg.persona?.name || msg.persona?.title ? (msg.persona.name || msg.persona.title) : Number(msg.gnomeId) === 0 ? 'Team Chat' : `Gnome ${msg.gnomeId}`}
                            </div>
                         )}

                         {/* Visual Attribution for Team Members */}
                         {msg.role === 'user' && msg.userFullName && msg.userId !== user?.id && (
                            <div className="text-[10px] font-bold text-charcoal-400 mb-1 tracking-wider text-right">
                                {msg.userFullName}
                            </div>
                         )}

                        {renderMessageContent(getMessageText(msg), (url, name) => handleSaveToIntelligence(url, name), msg.role === 'user')}
                        {msg.createdAt && (
                            <div className={`flex items-center justify-between mt-2`}>
                                <div className="flex items-center space-x-2">
                                    <div className={`text-[10px] ${msg.role === 'user' ? 'text-charcoal-400' : 'text-charcoal-400'}`}>
                                        {new Date(msg.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                                    </div>
                                    {msg.role === 'user' && (
                                         <button
                                             onClick={() => setReplyingTo(msg)}
                                             className="text-charcoal-400 hover:text-white opacity-0 group-hover:opacity-100 transition-opacity"
                                             aria-label="Reply to message"
                                             title="Reply"
                                         >
                                             <FiCornerUpLeft size={14} />
                                         </button>
                                    )}
                                </div>
                                {msg.role === 'assistant' && (
                                    <div className="flex items-center space-x-1">
                                        <button
                                            onClick={() => setReplyingTo(msg)}
                                            className="p-1.5 text-charcoal-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                            aria-label="Reply to message"
                                            title="Reply"
                                        >
                                            <FiCornerUpLeft size={16} />
                                        </button>
                                        <div className="w-px h-4 bg-charcoal-200 mx-1"></div>

                                        <button
                                            onClick={() => handleReaction(msg.id, 'heart', getMessageText(msg))}
                                            className={`p-1.5 rounded-md transition-colors ${msg.reactions?.includes('heart') || msg.reaction === 'heart' ? 'text-rose-600 bg-rose-50' : 'text-charcoal-400 hover:text-rose-600 hover:bg-rose-50'}`}
                                            aria-label="Add to Marketing Ideas book"
                                            title="Add to Marketing Ideas book"
                                        >
                                            <FaHeart size={16} />
                                        </button>
                                        <button
                                            onClick={(e) => {
                                                handleReaction(msg.id, 'rocket', getMessageText(msg));

                                                // Only send message if we are toggling ON
                                                const currentReactions = Array.isArray(msg.reactions) ? msg.reactions : (msg.reaction ? [msg.reaction] : []);
                                                if (!currentReactions.includes('rocket')) {
                                                    onFormSubmit(e, 'Proceed with your recommended task.');
                                                }
                                            }}
                                            className={`p-1.5 rounded-md transition-colors ${msg.reactions?.includes('rocket') || msg.reaction === 'rocket' ? 'text-blue-600 bg-blue-50' : 'text-charcoal-400 hover:text-blue-600 hover:bg-blue-50'}`}
                                            aria-label="Proceed with recommended task"
                                            title="Proceed with recommended task"
                                        >
                                            <FaRocket size={16} />
                                        </button>
                                        <div className="w-px h-4 bg-charcoal-200 mx-1"></div>

                                        <div className="w-px h-4 bg-charcoal-200 mx-1"></div>
                                        <button
                                            onClick={() => handleGenerateDocument(getMessageText(msg))}
                                            className="p-1.5 text-charcoal-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                            aria-label="Download as Document"
                                            title="Download as Document"
                                        >
                                            <FiFileText size={16} />
                                        </button>

                                        <div className="w-px h-4 bg-charcoal-200 mx-1"></div>
                                        <button
                                            onClick={() => handlePinTask(getMessageText(msg))}
                                            className="p-1.5 text-charcoal-400 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors"
                                            aria-label="Pin to Tasks"
                                            title="Pin to Tasks"
                                        >
                                            <BsPinAngle size={16} />
                                        </button>
                                        <button
                                            onClick={() => handleSaveTextToIntelligence(getMessageText(msg))}
                                            className="p-1.5 text-charcoal-400 hover:text-purple-600 hover:bg-purple-50 rounded-md transition-colors"
                                            aria-label="Save this response to The Vault"
                                            title="Save this response to The Vault"
                                        >
                                            <FiCpu size={16} />
                                        </button>

                                        <div className="w-px h-4 bg-charcoal-200 mx-1"></div>
                                        <button
                                            onClick={() => {
                                                if (speakingMessageId === msg.id) {
                                                    stopSpeaking();
                                                } else {
                                                    speakText(getMessageText(msg), msg.id);
                                                }
                                            }}
                                            disabled={isSynthesizing && speakingMessageId === msg.id}
                                            className={`p-1.5 rounded-md transition-colors ${speakingMessageId === msg.id ? 'text-blue-600 bg-blue-50' : 'text-charcoal-400 hover:text-blue-600 hover:bg-blue-50'} ${isSynthesizing && speakingMessageId === msg.id ? 'opacity-50 cursor-wait' : ''}`}
                                            aria-label={speakingMessageId === msg.id ? "Stop Reading" : "Read Aloud"}
                                            title={speakingMessageId === msg.id ? "Stop Reading" : "Read Aloud"}
                                        >
                                            {speakingMessageId === msg.id && isSynthesizing ? (
                                                <div className="w-4 h-4 border-2 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
                                            ) : speakingMessageId === msg.id ? (
                                                <FiVolumeX size={16} />
                                            ) : (
                                                <FiVolume2 size={16} />
                                            )}
                                        </button>
                                    </div>
                                )}
                            </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
            </>
        )}

        {/* Loading / Typing Indicator */}
        {isLoading && (
          <div className="flex justify-start">
             <div className="flex flex-row items-center">
              <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 overflow-hidden ${gnome.color} text-white mr-2`}>
                 {gnome.id === '0' || gnome.id === 0 ? (
                    <img src="/shroom-logo.webp" alt="Team" className="w-full h-full object-cover" />
                 ) : (gnome.headshot || gnome.image) ? (
                    <img src={gnome.headshot || gnome.image} alt={gnome.title} className="w-full h-full object-cover" />
                 ) : (
                    <SafeIcon icon={resolveIcon(gnome.icon)} className="text-sm" />
                 )}
              </div>
              <div className="bg-white border border-charcoal-200 p-3 rounded-2xl rounded-tl-none shadow-sm flex items-center space-x-1">
                <div className="w-2 h-2 bg-charcoal-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }}></div>
                <div className="w-2 h-2 bg-charcoal-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }}></div>
                <div className="w-2 h-2 bg-charcoal-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }}></div>
              </div>
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <form onSubmit={onFormSubmit} className="p-4 bg-white border-t border-charcoal-200">
        {replyingTo && (
            <div className="mb-2 px-3 py-2 bg-charcoal-50 border-l-4 border-blue-400 rounded-r flex justify-between items-center text-sm">
                <div className="flex items-center text-charcoal-600 overflow-hidden">
                    <FiCornerUpLeft className="mr-2 flex-shrink-0" />
                    <span className="truncate max-w-xs md:max-w-md">
                        Replying to: <span className="italic">{getMessageText(replyingTo).substring(0, 60)}...</span>
                    </span>
                </div>
                <button
                    type="button"
                    onClick={() => setReplyingTo(null)}
                    className="text-charcoal-400 hover:text-charcoal-600 p-1"
                    aria-label="Cancel reply"
                >
                    <FiX />
                </button>
            </div>
        )}

        {attachment && (
            <div className="mb-2 px-3 py-2 bg-blue-50 border border-blue-200 rounded-md flex justify-between items-center text-sm inline-block w-full max-w-sm">
                <div className="flex items-center text-blue-700 overflow-hidden">
                    <FiPaperclip className="mr-2 flex-shrink-0" />
                    <span className="truncate">{attachment.name}</span>
                </div>
                <button
                    type="button"
                    onClick={removeAttachment}
                    className="text-blue-400 hover:text-blue-600 p-1 ml-2"
                >
                    <FiX />
                </button>
            </div>
        )}
        <div className="flex items-end bg-charcoal-50 rounded-lg border border-charcoal-200 focus-within:border-charcoal-400 focus-within:ring-1 focus-within:ring-charcoal-400 transition-all">
          <input
              type="file"
              ref={fileInputRef}
              onChange={handleAttachmentChange}
              className="hidden"
              accept=".pdf,.doc,.docx,.txt,.md,.jpg,.jpeg,.png,.gif,.webp,.mp3,.mp4,.csv,.xls,.xlsx,.json"
              id={`file-upload-${gnome.id}`}
          />
          <label
              htmlFor={`file-upload-${gnome.id}`}
              className={`p-3 text-charcoal-400 hover:text-charcoal-600 cursor-pointer transition-colors ${uploadingAttachment ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
              <FiPaperclip size={20} />
          </label>
          <button
              type="button"
              onClick={() => setShowMediaPicker(true)}
              className="p-3 text-charcoal-400 hover:text-charcoal-600 transition-colors"
              title="Select from Asset Library"
          >
              <FiImage size={20} />
          </button>

          <textarea
            ref={textareaRef}
            rows={1}
            value={localInput}
            onChange={(e) => setLocalInput(autoCapitalize(e.target.value))}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                if (!isLoading && localInput.trim() && activeWorkspace && !authLoading) {
                  onFormSubmit(e);
                }
              }
            }}
            placeholder={`Ask ${getGnomeName(gnome.title)}...`}
            className="flex-1 bg-transparent border-none focus:ring-0 p-3 text-charcoal-900 placeholder-charcoal-400 outline-none resize-none min-h-[44px] max-h-[120px]"
            disabled={isLoading || !activeWorkspace || authLoading}
            aria-label="Message input"
          />
          <button
            type="submit"
            disabled={isLoading || !localInput.trim() || !activeWorkspace || authLoading}
            className="p-2 mr-2 mb-1 rounded-md bg-charcoal-900 text-white hover:bg-charcoal-800 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            aria-label="Send message"
          >
            <FiSend />
          </button>
        </div>
      </form>
    </div>
  );
});

export default GnomeChat;
